import type { Message } from '@ag-ui/client';
import { AG_UI_ROLE } from '../agent/ag-ui.constants';
import {
  isHttpStatusError,
  isNoBufferedRunError,
  isRunAlreadyActiveError,
  isUnauthorizedStreamError,
} from '../agent/panda-agent';
import type { PandaThreadDetail } from '../agent/panda-threads.api';
import type { PandaChatErrorKind } from '../panda-chat.types';

// React-free run state machine: drive a run, tell its endings apart, recover
// drops. The provider owns React state and injects it as callbacks.

// Generation continues server-side after a drop, so attempts are cheap replays.
// Delays sum to ~20s — the product-specified failure point.
export const RECONNECT_BACKOFF_MS = [1000, 2000, 4000, 8000, 16_000];
// Recovery is invisible below this threshold; past it the reconnecting message shows.
export const RECONNECT_INDICATOR_DELAY_MS = 3000;
// A dead connection often stalls silently instead of erroring — bound the
// silence. The run threshold is generous (no server heartbeat yet); a false
// positive only costs an invisible reconnect.
export const RUN_STALL_TIMEOUT_MS = 40_000;
export const CONNECT_STALL_TIMEOUT_MS = 5000;
// Hung attempts can't stretch recovery far past the ~35s failure point.
export const RECOVERY_DEADLINE_MS = 35000;
// `/connect` 204 fallback: the run's outcome persists at run end, so poll history.
export const HISTORY_POLL_ATTEMPTS = 3;
export const HISTORY_POLL_INTERVAL_MS = 3500;

const delay = (ms: number) => new Promise<void>(resolve => setTimeout(resolve, ms));

type RunOutcome = 'finished' | 'server_error' | 'dropped';

export type RefreshOutcome = 'success' | 'auth_failed' | 'network_error';

interface RunLifecycleSubscriber {
  onRunFinishedEvent?: () => void;
  // AG-UI passes the RunErrorEvent; we read only its `code` (e.g. unknown_offer).
  onRunErrorEvent?: (params?: unknown) => void;
}

/** Structural slice of PandaHttpAgent the lifecycle needs (fakeable in tests). */
export interface RecoverableAgent {
  threadId: string;
  messages: Message[];
  parentRunId?: string;
  runAgent(parameters?: undefined, subscriber?: RunLifecycleSubscriber): Promise<unknown>;
  connectAgent(parameters?: undefined, subscriber?: RunLifecycleSubscriber): Promise<unknown>;
  subscribe(subscriber: { onMessagesChanged?: () => void }): { unsubscribe: () => void };
  abortRun?(): void;
}

export interface RunLifecycleDeps<TAgent extends RecoverableAgent = RecoverableAgent> {
  /** The visible agent the run was started on. */
  agent: TAgent;
  createAgent: (options: {
    threadId?: string;
    initialMessages?: Message[];
    parentRunId?: string;
  }) => TAgent;
  /** Snapshot taken right after the user turn was appended — the replay baseline. */
  messagesBeforeRun: Message[];
  /** Id of this turn's user message; keys the history-poll acceptance check. */
  userMessageId: string;
  /** True once this send was superseded (clear chat) — go quiet, not to error. */
  cancelled: () => boolean;
  /**
   * Attach to an already-active run on 409. Only safe for a manual retry of the
   * same turn — a fresh send would adopt the previous turn's answer.
   */
  attachOnRunConflict?: boolean;
  recoverUnauthorized: boolean;
  refreshSession: () => Promise<RefreshOutcome>;
  fetchThread: (threadId: string) => Promise<PandaThreadDetail | null>;
  /** A recovery agent took over rendering — the provider should setAgent(it). */
  onAgentSwap: (agent: TAgent) => void;
  /**
   * Message mutation on a swapped-in recovery agent — @ag-ui snapshots
   * subscribers at connect start, so the host must re-render from here.
   */
  onMessagesChanged: () => void;
  onReconnectingChange: (reconnecting: boolean) => void;
  /** Off-screen agent currently re-attaching (or null) — clearChat aborts it. */
  onShadowChange: (shadow: TAgent | null) => void;
  onSettled: (agent: TAgent) => void;
  onFailed: (kind: PandaChatErrorKind, agent: TAgent) => void;
  /** A run failed with a server error code (RUN_ERROR `code`, or `run_already_active` on a 409). */
  onRunError?: (code?: string) => void;
  /**
   * Offer-accept path: swallow the generic error banner on a run-level rejection
   * (in-stream RUN_ERROR or a 409 `run_already_active`). The code is still surfaced
   * via `onRunError`, and the caller renders an offer-specific failure instead.
   * Connection drops are unaffected — they still surface normally.
   */
  suppressServerErrorBanner?: boolean;
  /** The session is unrecoverable (refresh rejected) — the host logs out. */
  onAuthFailed: () => void;
}

// Replays are byte-identical on the same snapshot, so (message count,
// last-message size) is a monotone progress measure — without re-serializing
// the whole list per event.
type Progress = readonly [count: number, lastMessageLength: number];
const progressOf = (messages: Message[]): Progress => {
  const last = messages[messages.length - 1];
  return [messages.length, last ? JSON.stringify(last).length : 0];
};
const hasCaughtUp = (progress: Progress, target: Progress) =>
  progress[0] !== target[0] ? progress[0] > target[0] : progress[1] >= target[1];

// A resolved run isn't a successful run: errors arrive as events, and a dropped
// connection can look like a normal end of stream — so the outcome comes from
// the terminal event, plus a stall timer for connections that just go silent.
async function driveAgent(
  agent: RecoverableAgent,
  mode: 'run' | 'connect',
  onRunError?: (code?: string) => void,
): Promise<RunOutcome> {
  let outcome: RunOutcome = 'dropped';
  const subscriber: RunLifecycleSubscriber = {
    onRunFinishedEvent: () => {
      outcome = 'finished';
    },
    onRunErrorEvent: params => {
      outcome = 'server_error';
      const code = (params as { event?: { code?: string | null } } | undefined)?.event?.code;
      onRunError?.(code ?? undefined);
    },
  };

  const stallMs = mode === 'run' ? RUN_STALL_TIMEOUT_MS : CONNECT_STALL_TIMEOUT_MS;
  let stalled = false;
  const onStall = () => {
    stalled = true;
    agent.abortRun?.();
  };
  let stallTimer = setTimeout(onStall, stallMs);
  const subscription = agent.subscribe({
    onMessagesChanged: () => {
      clearTimeout(stallTimer);
      stallTimer = setTimeout(onStall, stallMs);
    },
  });

  try {
    if (mode === 'connect') await agent.connectAgent(undefined, subscriber);
    else await agent.runAgent(undefined, subscriber);
  } catch (err) {
    // When the stall timer aborted the stream, this rejection is expected —
    // swallow it so the stall is reported as a drop rather than an error.
    if (!stalled) throw err;
  } finally {
    clearTimeout(stallTimer);
    subscription.unsubscribe();
  }
  return stalled ? 'dropped' : outcome;
}

// Accept history as this turn's recovery only if it answers our user message.
function hasAssistantReply(serverMessages: Message[], userMessageId: string): boolean {
  const userIndex = serverMessages.findIndex(m => m.id === userMessageId);
  if (userIndex < 0) return false;
  return serverMessages.slice(userIndex + 1).some(m => m.role === AG_UI_ROLE.ASSISTANT);
}

/**
 * Run one send to completion: initial run, 401-refresh re-run, drop recovery.
 * Never rejects.
 */
export async function executeRun<TAgent extends RecoverableAgent>(
  deps: RunLifecycleDeps<TAgent>,
): Promise<void> {
  const { agent, cancelled, recoverUnauthorized, refreshSession } = deps;

  const handleOutcome = async (outcome: RunOutcome) => {
    // clearChat's abort surfaces as a synthetic RUN_ERROR on a *resolved* run —
    // the resolved path needs the guard too.
    if (cancelled()) return;
    if (outcome === 'finished') deps.onSettled(agent);
    // onRunError already fired inside driveAgent with the code; the offer path
    // suppresses the generic banner and renders its own failure instead.
    else if (outcome === 'server_error') {
      if (!deps.suppressServerErrorBanner) deps.onFailed('generic', agent);
    } else await recoverRun(deps);
  };

  // Shared triage — the initial run and the post-refresh re-run must recover
  // drops identically.
  const classifyAndHandle = async (err: unknown) => {
    if (isRunAlreadyActiveError(err)) {
      // Attach only when the active run is this turn's own (manual retry).
      if (deps.attachOnRunConflict) await recoverRun(deps);
      else {
        deps.onRunError?.('run_already_active');
        if (!deps.suppressServerErrorBanner) deps.onFailed('generic', agent);
      }
    } else if (isHttpStatusError(err)) {
      // Rejected pre-stream — no run registered, nothing to reconnect to.
      deps.onFailed('generic', agent);
    } else {
      // Mid-stream drop; the server keeps generating — re-attach.
      await recoverRun(deps);
    }
  };

  try {
    await handleOutcome(await driveAgent(agent, 'run', deps.onRunError));
  } catch (err) {
    if (cancelled()) return;
    // Raw SSE bypasses axios's refresh interceptor, so on 401 refresh once and
    // re-run (panda authorizes before registering the run, so nothing started).
    if (recoverUnauthorized && isUnauthorizedStreamError(err)) {
      const refresh = await refreshSession();
      if (cancelled()) return;
      if (refresh === 'success') {
        try {
          await handleOutcome(await driveAgent(agent, 'run', deps.onRunError));
        } catch (retryErr) {
          if (cancelled()) return;
          await classifyAndHandle(retryErr);
        }
      } else {
        deps.onFailed('generic', agent);
        // Invalid refresh token is unrecoverable; NETWORK_ERROR survives offline.
        if (refresh === 'auth_failed') deps.onAuthFailed();
      }
    } else {
      await classifyAndHandle(err);
    }
  }
}

/**
 * Manual retry of a turn that failed mid-stream: the server-side run still
 * exists or has persisted — re-enter recovery instead of starting a new run.
 */
export async function resumeRun<TAgent extends RecoverableAgent>(
  deps: RunLifecycleDeps<TAgent>,
): Promise<void> {
  await recoverRun(deps);
}

// Re-attach to the still-running answer: replay into a fresh shadow agent (the
// verifier rejects replays into one holding the partial turn) and swap it in
// at catch-up — the answer pauses and continues, never re-streams.
async function recoverRun<TAgent extends RecoverableAgent>(
  deps: RunLifecycleDeps<TAgent>,
): Promise<void> {
  const { cancelled, createAgent, messagesBeforeRun } = deps;

  const indicatorTimer = setTimeout(() => {
    if (!cancelled()) deps.onReconnectingChange(true);
  }, RECONNECT_INDICATOR_DELAY_MS);

  try {
    let visible = deps.agent;
    let visibleProgress = progressOf(visible.messages);
    const recoveryStartedAt = Date.now();

    for (const backoff of RECONNECT_BACKOFF_MS) {
      await delay(backoff);
      if (cancelled()) return;
      // Hung attempts eat wall-clock the backoff schedule doesn't account for.
      if (Date.now() - recoveryStartedAt >= RECOVERY_DEADLINE_MS) break;

      const shadow = createAgent({
        threadId: visible.threadId,
        initialMessages: messagesBeforeRun,
        // Recovery must not drop the Langfuse join — every later run sends it.
        parentRunId: deps.agent.parentRunId,
      });
      deps.onShadowChange(shadow);
      const targetProgress = visibleProgress;
      let swapped = false;
      const swapIfCaughtUp = (force = false) => {
        // abortRun teardown is async — a late event must not swap a cleared chat back in.
        if (swapped || cancelled()) return;
        if (!force && !hasCaughtUp(progressOf(shadow.messages), targetProgress)) return;
        swapped = true;
        deps.onAgentSwap(shadow);
      };
      const subscription = shadow.subscribe({
        onMessagesChanged: () => {
          swapIfCaughtUp();
          // Once on screen, the host re-renders from here (see deps.onMessagesChanged).
          if (swapped && !cancelled()) deps.onMessagesChanged();
        },
      });

      try {
        const outcome = await driveAgent(shadow, 'connect');
        if (cancelled()) return;
        if (outcome === 'finished') {
          swapIfCaughtUp(true);
          deps.onSettled(shadow);
          return;
        }
        if (outcome === 'server_error') {
          swapIfCaughtUp(true);
          deps.onFailed('generic', shadow);
          return;
        }
        // Dropped again; a swapped shadow is the new visible to catch up to.
        if (swapped) {
          visible = shadow;
          visibleProgress = progressOf(shadow.messages);
        }
      } catch (err) {
        if (cancelled()) return;
        if (isNoBufferedRunError(err)) {
          // Buffer on another instance or expired — recover from persisted history.
          await pollHistory(deps, visible);
          return;
        }
        if (deps.recoverUnauthorized && isUnauthorizedStreamError(err)) {
          const refresh = await deps.refreshSession();
          if (cancelled()) return;
          if (refresh === 'auth_failed') {
            deps.onFailed('connection', visible);
            deps.onAuthFailed();
            return;
          }
          // 'success' re-attempts with the fresh token; 'network_error' keeps
          // trying — offline sessions must survive (never logout).
        }
        // Other failures burn the attempt and fall through to the next one.
      } finally {
        subscription.unsubscribe();
        // Late teardown of a cancelled run must not clobber a newer run's state.
        if (!cancelled()) deps.onShadowChange(null);
      }
    }

    if (!cancelled()) deps.onFailed('connection', visible);
  } finally {
    clearTimeout(indicatorTimer);
    if (!cancelled()) deps.onReconnectingChange(false);
  }
}

async function pollHistory<TAgent extends RecoverableAgent>(
  deps: RunLifecycleDeps<TAgent>,
  visible: TAgent,
): Promise<void> {
  const { cancelled } = deps;
  for (let attempt = 0; attempt < HISTORY_POLL_ATTEMPTS; attempt++) {
    if (attempt > 0) await delay(HISTORY_POLL_INTERVAL_MS);
    if (cancelled()) return;
    try {
      const detail = await deps.fetchThread(visible.threadId);
      if (cancelled()) return;
      if (detail && hasAssistantReply(detail.messages, deps.userMessageId)) {
        const recovered = deps.createAgent({
          threadId: visible.threadId,
          initialMessages: detail.messages,
          parentRunId: deps.agent.parentRunId,
        });
        deps.onAgentSwap(recovered);
        deps.onSettled(recovered);
        return;
      }
      // No reply for this turn yet — the run may still be generating; keep polling.
    } catch {
      // Transient (still offline, 5xx) — keep polling.
    }
  }
  if (!cancelled()) deps.onFailed('connection', visible);
}
